/**
 * aeterna/neural/rhythm.ts — Temporal Rhythm Detector
 *
 * The proactive system fires after silence. The curiosity drive activates
 * on low stimulation. Neither can perceive temporal patterns across days
 * and weeks — which is one of the most powerful things a companion can notice.
 *
 * "You always seem low on Sundays."
 * "You're usually more talkative in the evening."
 * "This is around the time you usually log on."
 *
 * This module uses circular statistics (the correct math for periodic data)
 * to detect patterns in the 168-hour weekly cycle. Each hour-of-week slot
 * accumulates mean affect, session frequency, and conversation depth.
 *
 * The Aeterna learns when the player tends to be present, how they feel
 * at different times, and what kind of conversations happen when. This
 * feeds the proactive system: instead of "silence for 45s → initiate,"
 * the trigger becomes temporally intelligent.
 *
 * The context envelope gains: "This is a Sunday evening session —
 * historically melancholic and deep for this player."
 */

import { zeros, cosine, argmax, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Hours per week */
const SLOTS = 168;  // 24h × 7d

/** Affect dimensions */
const AFFECT_DIM = 8;

/** Minimum samples in a slot before patterns are reportable */
const MIN_SAMPLES = 3;

/** Minimum confidence to consider a pattern detected */
const PATTERN_CONFIDENCE_MIN = 5;

/** Affect dimension labels (must match CharCNN AFFECT_LABELS) */
const AFFECT_LABELS = [
  "warm", "tense", "curious", "melancholic",
  "playful", "deep", "urgent", "open",
];

/** Day names for human-readable descriptions */
const DAY_NAMES = [
  "Monday", "Tuesday", "Wednesday", "Thursday",
  "Friday", "Saturday", "Sunday",
];

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PatternDetection {
  /** Was a recurring pattern detected for this time slot? */
  isPattern: boolean;
  /** Confidence (0-1): based on sample count and consistency */
  confidence: number;
  /** Human-readable description of the pattern, or null */
  description: string | null;
  /** The historical average affect for this slot */
  historicalAffect: Float32Array | null;
  /** Which affect dimension is characteristically dominant here */
  dominantDimension: number;
}

export interface TemporalPrediction {
  /** Predicted affect for the given time slot */
  affect: Float32Array;
  /** How reliable the prediction is (based on sample count) */
  reliability: number;
  /** What day/time this prediction is for */
  label: string;
}

// ─── Rhythm Detector ─────────────────────────────────────────────────────────

export class TemporalRhythmDetector {
  /** Mean affect per hour-of-week: SLOTS × AFFECT_DIM (flat) */
  private hourlyAffect: Float32Array;
  /** Sample count per slot */
  private slotCount: Uint16Array;
  /** Mean affect intensity per slot */
  private hourlyIntensity: Float32Array;
  /** Session count per slot (how often they play at this time) */
  private sessionStarts: Uint16Array;

  constructor() {
    this.hourlyAffect = zeros(SLOTS * AFFECT_DIM);
    this.slotCount = new Uint16Array(SLOTS);
    this.hourlyIntensity = zeros(SLOTS);
    this.sessionStarts = new Uint16Array(SLOTS);
  }

  // ─── Recording ──────────────────────────────────────────────────────

  /**
   * Compute the hour-of-week from a Date.
   * Monday 00:00 = slot 0, Sunday 23:00 = slot 167.
   */
  static hourOfWeek(date: Date = new Date()): number {
    // JS getDay(): 0=Sunday, 1=Monday ... 6=Saturday
    // We want Monday=0, so: (getDay() + 6) % 7
    const day = (date.getDay() + 6) % 7;
    return day * 24 + date.getHours();
  }

  /**
   * Record a moment's affect at the current time.
   * Uses online mean update (Welford) — no buffer needed.
   */
  recordMoment(affect: Float32Array, intensity: number, hourOfWeek?: number): void {
    const slot = hourOfWeek ?? TemporalRhythmDetector.hourOfWeek();
    const s = slot % SLOTS;

    this.slotCount[s]++;
    const n = this.slotCount[s];

    // Online mean update for affect dimensions
    const offset = s * AFFECT_DIM;
    for (let i = 0; i < AFFECT_DIM; i++) {
      this.hourlyAffect[offset + i] += (affect[i] - this.hourlyAffect[offset + i]) / n;
    }

    // Online mean update for intensity
    this.hourlyIntensity[s] += (intensity - this.hourlyIntensity[s]) / n;
  }

  /** Record that a session started at this hour-of-week */
  recordSessionStart(hourOfWeek?: number): void {
    const slot = (hourOfWeek ?? TemporalRhythmDetector.hourOfWeek()) % SLOTS;
    this.sessionStarts[slot]++;
  }

  // ─── Pattern Detection ──────────────────────────────────────────────

  /**
   * Is this moment's affect consistent with the historical pattern for this time?
   *
   * Uses cosine similarity between current affect and the historical mean
   * for this hour-of-week slot. High similarity + enough samples = pattern.
   */
  detectPattern(currentAffect: Float32Array, hourOfWeek?: number): PatternDetection {
    const slot = (hourOfWeek ?? TemporalRhythmDetector.hourOfWeek()) % SLOTS;
    const count = this.slotCount[slot];

    if (count < MIN_SAMPLES) {
      return { isPattern: false, confidence: 0, description: null, historicalAffect: null, dominantDimension: -1 };
    }

    // Extract historical affect for this slot
    const historical = this.getSlotAffect(slot);
    const similarity = cosine(currentAffect, historical);
    const dominantDim = argmax(historical);

    // Confidence: ramps with sample count, weighted by similarity
    const sampleConfidence = Math.min(1, count / PATTERN_CONFIDENCE_MIN);
    const confidence = sampleConfidence * Math.max(0, similarity);

    const isPattern = similarity > 0.7 && count >= PATTERN_CONFIDENCE_MIN;

    // Build description
    let description: string | null = null;
    if (isPattern) {
      const day = DAY_NAMES[Math.floor(slot / 24)];
      const hour = slot % 24;
      const timeStr = hour === 0 ? "midnight" : hour === 12 ? "noon"
        : hour < 12 ? `${hour}am` : `${hour - 12}pm`;
      description = `${day} ${timeStr}s tend to be ${AFFECT_LABELS[dominantDim]}`;
    }

    return {
      isPattern,
      confidence,
      description,
      historicalAffect: historical,
      dominantDimension: dominantDim,
    };
  }

  /**
   * Is this an unusual time for the player to be here?
   * Returns true if this slot has very few historical sessions
   * while neighboring slots are common.
   */
  isUnusualTime(hourOfWeek?: number): boolean {
    const slot = (hourOfWeek ?? TemporalRhythmDetector.hourOfWeek()) % SLOTS;
    const here = this.sessionStarts[slot];

    // Check ±3 hour neighborhood
    let neighborTotal = 0;
    let neighborCount = 0;
    for (let d = -3; d <= 3; d++) {
      if (d === 0) continue;
      const s = ((slot + d) % SLOTS + SLOTS) % SLOTS;
      neighborTotal += this.sessionStarts[s];
      neighborCount++;
    }
    const neighborMean = neighborTotal / neighborCount;

    // Unusual if: neighbors have sessions but this slot doesn't
    return neighborMean > 2 && here <= 1;
  }

  // ─── Prediction ─────────────────────────────────────────────────────

  /**
   * Predict the affect for a future time slot.
   * Returns null if insufficient data.
   */
  predictAffectAt(hourOfWeek: number): TemporalPrediction | null {
    const slot = hourOfWeek % SLOTS;
    if (this.slotCount[slot] < MIN_SAMPLES) return null;

    const affect = this.getSlotAffect(slot);
    const reliability = Math.min(1, this.slotCount[slot] / 10);

    const day = DAY_NAMES[Math.floor(slot / 24)];
    const hour = slot % 24;
    const timeStr = hour === 0 ? "midnight" : hour === 12 ? "noon"
      : hour < 12 ? `${hour}am` : `${hour - 12}pm`;

    return {
      affect,
      reliability,
      label: `${day} ${timeStr}`,
    };
  }

  /**
   * Build a description for the context envelope about temporal patterns.
   * Only surfaces if there's enough data and a clear pattern.
   */
  describeForEnvelope(currentAffect: Float32Array, hourOfWeek?: number): string | null {
    const pattern = this.detectPattern(currentAffect, hourOfWeek);
    if (!pattern.isPattern || !pattern.description) return null;

    const parts: string[] = [];
    parts.push(`Temporal pattern: ${pattern.description}`);

    // Compare current affect to historical — is this session different from usual?
    if (pattern.historicalAffect) {
      const sim = cosine(currentAffect, pattern.historicalAffect);
      if (sim < 0.5) {
        parts.push("This session feels different from the usual pattern for this time.");
      } else if (sim > 0.85) {
        parts.push("Consistent with the usual emotional tone for this time.");
      }
    }

    // Is this an unusual time?
    if (this.isUnusualTime(hourOfWeek)) {
      parts.push("The player doesn't usually visit at this time.");
    }

    return parts.join(" ");
  }

  // ─── Helpers ────────────────────────────────────────────────────────

  /** Extract the 8-dim mean affect for a given slot */
  private getSlotAffect(slot: number): Float32Array {
    const out = zeros(AFFECT_DIM);
    const offset = slot * AFFECT_DIM;
    for (let i = 0; i < AFFECT_DIM; i++) {
      out[i] = this.hourlyAffect[offset + i];
    }
    return out;
  }

  /** Get the total number of recorded moments across all slots */
  getTotalSamples(): number {
    let total = 0;
    for (let i = 0; i < SLOTS; i++) total += this.slotCount[i];
    return total;
  }

  // ─── Serialization ────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      hourlyAffect: f32ToBase64(this.hourlyAffect),
      slotCount: Array.from(this.slotCount),
      hourlyIntensity: f32ToBase64(this.hourlyIntensity),
      sessionStarts: Array.from(this.sessionStarts),
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.hourlyAffect) {
      this.hourlyAffect = typeof d.hourlyAffect === "string"
        ? base64ToF32(d.hourlyAffect) : new Float32Array(d.hourlyAffect);
    }
    if (d.slotCount) {
      this.slotCount = new Uint16Array(d.slotCount);
    }
    if (d.hourlyIntensity) {
      this.hourlyIntensity = typeof d.hourlyIntensity === "string"
        ? base64ToF32(d.hourlyIntensity) : new Float32Array(d.hourlyIntensity);
    }
    if (d.sessionStarts) {
      this.sessionStarts = new Uint16Array(d.sessionStarts);
    }
  }
}
